<template>
    <div class="contact-form-section pb-100">
        <div class="container">
            <div class="contact-form-box">
                <div class="row">
                    <div class="col-lg-6">
                        <div class="contact-map contact-form-item">
                            <!-- Map info tooltip -->
                            <div
                                v-if="!isMapError"
                                class="map-info-card"
                            >
                                <div class="map-info-content">
                                    <div class="map-info-title">{{ mapCopy.company }}</div>
                                    <div class="map-info-subtitle">{{ mapCopy.addressLabel }}</div>
                                    <div class="map-info-address">
                                        <i class="icofont-location-pin map-info-address-icon"></i>
                                        <span class="map-info-address-text">
                                            {{ mapCopy.address }}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    class="map-info-go-btn"
                                    @click="openMapLink"
                                >
                                    <span class="map-info-go-arrow">
                                        <img v-lazy="dirNewIcon"
                                            :alt="mapCopy.ctaAlt"
                                            class="map-info-go-icon"
                                        />
                                    </span>
                                    <span class="map-info-go-text">
                                        {{ mapCopy.cta }}
                                    </span>
                                </button>
                            </div>

                            <!-- Primary map: Gaode interactive map -->
                            <div
                                ref="amapContainer"
                                class="amap-container"
                                v-show="!isMapError"
                            ></div>

                            <!-- Fallback: static map -->
                            <div
                                v-if="isMapError"
                                class="fallback-map"
                            >
                                <h3 class="fallback-title">{{ mapCopy.fallbackTitle }}</h3>
                                <img
                                    v-if="staticMapUrl"
                                    v-lazy="staticMapUrl"
                                    :alt="mapCopy.fallbackTitle"
                                    class="fallback-image"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="col-lg-6">
                        <div class="contact-form-item desk-pad-left-40">
                            <div class="section-title text-start section-title-left">
                                <h2>{{ formCopy.title }}</h2>
                                <p>{{ formCopy.subtitle }}</p>
                            </div>
                            <!-- Success / error message -->
                            <transition name="message-fade">
                                <div v-if="messageStatus" class="alert message-alert" :class="messageStatus.type === 'success' ? 'alert-success' : 'alert-danger'">
                                    <i :class="messageStatus.type === 'success' ? 'icofont-check-circled' : 'icofont-close-circled'"></i>
                                    {{ messageStatus.text }}
                                </div>
                            </transition>
                            <form class="contact-form" @submit.prevent="handleSubmit">
                                <div class="form-group mb-20">
                                    <input 
                                        type="text" 
                                        name="name" 
                                        id="name" 
                                        class="form-control" 
                                        :placeholder="placeholders.name"
                                        v-model="formData.name"
                                    />
                                </div>
                                <div class="form-group mb-20">
                                    <input 
                                        type="email" 
                                        name="email" 
                                        id="email" 
                                        class="form-control" 
                                        :placeholder="placeholders.email"
                                        v-model="formData.email"
                                    />
                                </div>
                                <div class="form-group mb-20">
                                    <input 
                                        type="tel" 
                                        name="phone" 
                                        id="phone" 
                                        class="form-control" 
                                        :placeholder="placeholders.phone"
                                        v-model="formData.phone"
                                    />
                                </div>
                                <div class="form-group mb-20">
                                    <input 
                                        type="text" 
                                        name="subject" 
                                        id="subject" 
                                        class="form-control" 
                                        :placeholder="placeholders.subject"
                                        v-model="formData.subject"
                                    />
                                </div>
                                <div class="form-group mb-20">
                                    <textarea 
                                        name="message" 
                                        class="form-control" 
                                        id="message" 
                                        rows="3" 
                                        :placeholder="placeholders.message"
                                        v-model="formData.message"
                                    ></textarea>
                                </div>
                                <div class="form-button">
                                    <button 
                                        class="btn main-btn full-width" 
                                        type="submit"
                                        :disabled="isSubmitting"
                                    >
                                        {{ isSubmitting ? formCopy.submitting : formCopy.button }}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import { FEISHU_CONFIG, FeishuUtils } from '@/config/feishu.js'
import { API_KEY } from '@/config/apiKey.js'

// Gaode Map API key
const AMAP_KEY = API_KEY.AMAP_KEY
const AMAP_ES5_URL = 'https://cache.amap.com/lbs/static/es5.min.js'
const AMAP_SDK_URL = `https://webapi.amap.com/maps?v=1.4.15&key=${AMAP_KEY}`

const scriptPromises = {}
let amapReadyPromise = null

function loadExternalScript(src) {
    if (typeof document === 'undefined') {
        return Promise.reject(new Error('Document is not available for script injection'))
    }

    if (!scriptPromises[src]) {
        scriptPromises[src] = new Promise((resolve, reject) => {
            const existing = document.querySelector(`script[src="${src}"]`)
            if (existing) {
                if (existing.getAttribute('data-loaded') === 'true') {
                    resolve()
                } else {
                    existing.addEventListener('load', resolve, { once: true })
                    existing.addEventListener('error', reject, { once: true })
                }
                return
            }

            const script = document.createElement('script')
            script.src = src
            script.async = true
            script.onload = () => {
                script.setAttribute('data-loaded', 'true')
                resolve()
            }
            script.onerror = () => reject(new Error(`Failed to load script: ${src}`))
            document.head.appendChild(script)
        }).catch((error) => {
            delete scriptPromises[src]
            throw error
        })
    }

    return scriptPromises[src]
}

function ensureAmapReady() {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('Window is not defined'))
    }

    if (!amapReadyPromise) {
        amapReadyPromise = (async () => {
            await loadExternalScript(AMAP_ES5_URL)
            await loadExternalScript(AMAP_SDK_URL)
            if (!window.AMap) {
                throw new Error('AMap SDK failed to initialize')
            }
            return window.AMap
        })().catch((error) => {
            amapReadyPromise = null
            throw error
        })
    }

    return amapReadyPromise
}

export default {
    name: 'ContactForm',
    data() {
        return {
            formData: {
                name: '',
                email: '',
                phone: '',
                subject: '',
                message: ''
            },
            // Map state
            isMapError: false,
            staticMapUrl: '',
            isSubmitting: false,
            messageStatus: null,
            mapInstance: null
        }
    },
    mounted() {
        this.loadAMapScript()
    },
    beforeUnmount() {
        if (this.mapInstance) {
            this.mapInstance.destroy()
            this.mapInstance = null
        }
    },
    computed: {
        mapCopy() {
            const map = this.$t('contact.map')
            return typeof map === 'object' ? map : {}
        },
        dirNewIcon() {
            return this.$ossImg('dirnew_white.png')
        },
        formCopy() {
            const form = this.$t('contact.form')
            return typeof form === 'object' ? form : {}
        },
        placeholders() {
            return this.formCopy.placeholders || {}
        },
        mapLanguageCode() {
            const locale =
                (this.$y18n && this.$y18n.locale && this.$y18n.locale.value) ||
                (typeof this.$getLang === 'function' ? this.$getLang() : 'zh')
            return locale === 'en' ? 'en' : 'zh_cn'
        }
    },
    watch: {
        mapLanguageCode(newLang) {
            this.setMapLanguage(newLang)
        }
    },
    methods: {
        async handleSubmit(event) {
            // Prevent default submit
            event.preventDefault()
            
            // Validate inputs
            if (!this.validateForm()) {
                return
            }

            this.isSubmitting = true
            this.messageStatus = null

            try {
                // Send notification to Feishu
                await this.sendToFeishu()
                
                // Redirect to success page
                setTimeout(() => {
                    this.$router.push('/contact-success')
                }, 1000)
                
            } catch (error) {
                console.error('Error sending message:', error)
                this.messageStatus = {
                    type: 'error',
                    text: this.getFormMessage('error')
                }
                this.isSubmitting = false
            }
        },

        validateForm() {
            const { name, email, phone, subject, message } = this.formData
            
            if (!name.trim()) {
                this.showError(this.getValidationMessage('name'))
                return false
            }
            
            if (!email.trim() || !this.isValidEmail(email)) {
                this.showError(this.getValidationMessage('email'))
                return false
            }
            
            if (!phone.trim()) {
                this.showError(this.getValidationMessage('phone'))
                return false
            }
            
            if (!subject.trim()) {
                this.showError(this.getValidationMessage('subject'))
                return false
            }
            
            if (!message.trim()) {
                this.showError(this.getValidationMessage('message'))
                return false
            }
            
            return true
        },

        isValidEmail(email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            return emailRegex.test(email)
        },

        showError(message) {
            this.messageStatus = {
                type: 'error',
                text: message
            }
        },

        getValidationMessage(field) {
            const custom = this.formCopy.validation && this.formCopy.validation[field]
            const fallback = this.$t(`contact.form.validation.${field}`)
            return custom || fallback || ''
        },

        getFormMessage(key) {
            const custom = this.formCopy.messages && this.formCopy.messages[key]
            const fallback = this.$t(`contact.form.messages.${key}`)
            return custom || fallback || ''
        },

        async sendToFeishu() {
            // Use webhook from config
            const webhookUrl = FEISHU_CONFIG.WEBHOOK_URL
            
            // Ensure webhook is configured
            if (webhookUrl.includes('YOUR_WEBHOOK_URL')) {
                throw new Error('Please configure the Feishu webhook URL in src/config/feishu.js')
            }
            
            // Format message payload
            const sourceLabel = this.formCopy.source || 'YOGEE Website'
            const messageContent = FeishuUtils.formatContactMessage(this.formData, sourceLabel)
            const message = FeishuUtils.buildMessage(messageContent)

            const response = await axios.post(webhookUrl, message, FEISHU_CONFIG.API_CONFIG)

            if (response.data.code !== 0) {
                throw new Error(`Feishu API error: ${response.data.msg}`)
            }

            return response.data
        },

        openMapLink() {
            if (typeof window === 'undefined') return
            window.open('https://surl.amap.com/cuNQHwZu4q0', '_blank')
        },

        // ================= Map helpers =================
        async loadAMapScript() {
            if (typeof window === 'undefined') return

            try {
                const AMap = await ensureAmapReady()
                this.initAMap(AMap)
            } catch (error) {
                this.handleMapError(error)
            }
        },

        initAMap(AMap) {
            try {
                if (!this.$refs.amapContainer) {
                    throw new Error('Map container not found')
                }

                const map = new AMap.Map(this.$refs.amapContainer, {
                    zoom: 16,
                    center: [120.165235, 30.327904],
                    viewMode: '2D',
                    lang: this.mapLanguageCode
                })

                const marker = new AMap.Marker({
                    position: [120.165235, 30.327904],
                    title: this.mapCopy.markerTitle || 'Hanzhiyun Business Center Building 1',
                    icon: new AMap.Icon({
                        size: new AMap.Size(25, 40),
                        image: 'https://webapi.amap.com/theme/v1.3/markers/n/mark_r.png',
                        imageSize: new AMap.Size(25, 40)
                    })
                })

                map.add(marker)
                if (typeof AMap.plugin === 'function') {
                    AMap.plugin(['AMap.Scale'], () => {
                        map.addControl(new AMap.Scale())
                    })
                }
                this.isMapError = false
                this.mapInstance = map
                this.setMapLanguage(this.mapLanguageCode)
            } catch (error) {
                this.handleMapError(error)
            }
        },
        setMapLanguage(lang) {
            if (!lang) return
            if (this.mapInstance && typeof this.mapInstance.setLang === 'function') {
                this.mapInstance.setLang(lang)
            }
        },

        handleMapError(error) {
            console.error('AMap load/init error:', error)
            this.isMapError = true
            this.staticMapUrl = `https://restapi.amap.com/v3/staticmap?location=120.165235,30.327904&zoom=16&size=800*600&markers=mid,0xFF0000,A:120.165235,30.327904&key=${AMAP_KEY}`
        },

        resetForm() {
            this.formData = {
                name: '',
                email: '',
                phone: '',
                subject: '',
                message: ''
            }
        }
    }
}
</script>

<style scoped>
/* Message alert styles */
.message-alert {
    margin-bottom: 20px;
    padding: 12px 16px;
    border-radius: 6px;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.message-alert.alert-success {
    background-color: #d4edda;
    border: 1px solid #c3e6cb;
    color: #155724;
}

.message-alert.alert-danger {
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    color: #721c24;
}

.message-alert i {
    font-size: 16px;
}

/* Map container used for positioning */
.contact-map {
    position: relative;
}

/* Map info card styles */
.map-info-card {
    position: absolute;
    top: 20px;
    left: 20px;
    width: 280px;
    max-width: calc(100% - 40px);
    background-color: #ffffff;
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
    font-size: 12px;
    z-index: 5;
}

.map-info-content {
    padding: 16px 14px;
    /* Reserve space for the round CTA button */
    padding-right: 72px;
}

.map-info-title {
    font-size: 14px;
    font-weight: 600;
    color: #333333;
    margin-bottom: 4px;
}

.map-info-subtitle {
    font-size: 12px;
    color: #999999;
    margin-bottom: 8px;
}

.map-info-address {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #555555;
}

.map-info-address-icon {
    font-size: 14px;
    color: #666666;
}

.map-info-address-text {
    font-size: 12px;
    line-height: 1.4;
}

.map-info-go-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding: 4px 0;
    border: none;
    border-radius: 50%;
    background-color: #0B71FA;
    color: #ffffff;
    cursor: pointer;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.25);
}

.map-info-go-btn:focus {
    outline: none;
}

.map-info-go-arrow {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    margin-bottom: 2px;
}

.map-info-go-icon {
    width: 16px;
    height: 16px;
    display: block;
}

.map-info-go-text {
    font-size: 10px;
    line-height: 1;
}

/* Map container & fallback styles */
.amap-container {
    width: 100%;
    height: 100%;
    min-height: 400px;
}

.fallback-map {
    text-align: center;
    padding: 20px;
    background: #f5f5f5;
}

.fallback-title {
    font-size: 16px;
    margin-bottom: 12px;
}

.fallback-image {
    width: 100%;
    border-radius: 6px;
}

/* Fade animation */
.message-fade-enter-active, .message-fade-leave-active {
    transition: all 0.3s ease;
}

.message-fade-enter-from {
    opacity: 0;
    transform: translateY(-10px);
}

.message-fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

/* Disabled button styles */
.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
.form-button .main-btn {
    background-color: #0B71FA;
    color: white;
    transition: all 0.5s ease-in-out;
}
.form-button .main-btn:hover {
    background: linear-gradient(90deg, #0B71FA 0%, #FF94C8 100%);
    color: white;
}
</style>
